import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import { default as MuiSwitch } from '@material-ui/core/Switch'
import type { SwitchProps as MuiSwitchProps } from '@material-ui/core/Switch'
import classnames from 'classnames'

const useStyles = makeStyles<Theme, SwitchProps>((theme) => ({
  root: {
    pointerEvents: ({ readOnly }: any) => (readOnly ? 'none' : 'auto'),
    position: 'relative',
    width: 42,
    height: 22,
    padding: 0,
    margin: theme.spacing(),
    '&::before': {
      content: (props) => (props.overlay ? `"${props.overlay}"` : ''),
      position: 'absolute',
      color: (props) => {
        if (props.disabled) {
          return theme.paintscout.palette.brand.Neutral.P70
        }

        return props.checked ? theme.palette.common.white : theme.paintscout.palette.brand.Neutral.P35
      },
      ...theme.typography.overline,
      top: '50%',
      left: (props) => (props.checked ? '0.6rem' : 'auto'),
      right: (props) => (props.checked ? 'auto' : '0.5rem'),
      transform: 'translateY(-50%)'
    }
  },
  sizeSmall: {
    '& $switchBase': {
      padding: 1
    },
    '& $switchBase$checked': {
      transform: 'translateX(18px)'
    },
    '& $thumb': {
      width: 16,
      height: 16
    },
    '&$root': {
      height: 18,
      width: 36
    },
    '&$root::before': {
      fontSize: '0.75em'
    }
  },
  switchBase: {
    padding: 1,
    '&$checked': {
      '&:not($disabled)': {
        color: theme.palette.common.white
      },
      '&:not($disabled) + $track': {
        // Green track when toggled on (the default). `switchColor="primary"` overrides this to blue via
        // the `primary` modifier class below — kept as a *static* rule because a function value here
        // failed to apply, leaving the checked track the same grey as the off state.
        backgroundColor: theme.paintscout.palette.semantic.Success.S70,
        opacity: 1,
        border: 'none'
      }
    },
    // Keep disabled switches visible. MUI's default drops the disabled track to ~0.12 opacity, which
    // renders near-white on a light background (effectively invisible). This 3-class selector
    // outranks MUI's default rule, so a disabled track stays a legible muted grey.
    '&$disabled + $track': {
      opacity: 0.55
    }
  },
  thumb: {
    width: 20,
    height: 20
  },
  track: {
    boxSizing: 'border-box',
    borderRadius: 13,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P80}`,
    backgroundColor: theme.paintscout.palette.brand.Neutral.P85,
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border'])
  },
  checked: {},
  disabled: {},
  // Blue checked-track variant for `switchColor="primary"`. Applied to the root via className; the
  // extra class + descendant selector outranks the default green rule, so blue wins statically.
  primary: {
    '& $switchBase$checked:not($disabled) + $track': {
      backgroundColor: theme.palette.primary.main
    }
  }
}))

export interface SwitchProps extends Omit<MuiSwitchProps, 'inputProps'> {
  overlay?: string
  /** Checked track color. Defaults to the green success color; `'primary'` renders it blue. */
  switchColor?: 'success' | 'primary'
  inputProps?: MuiSwitchProps & { 'data-testid'?: string }
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(props: SwitchProps, ref) {
  // `primary` is a root modifier applied via className, not a MUI Switch classes key — keep it out of
  // the `classes` object (MUI would warn on an unknown key) and out of the DOM.
  const { primary, ...classes } = useStyles(props)
  // `switchColor` is a styling-only prop — strip it so it isn't forwarded to the DOM.
  const { switchColor, className, ...muiProps } = props
  // Default to color="default": MUI's default color is "secondary", and this app sets
  // secondary.main to white (get-theme.ts), so a checked switch's fallback track renders white
  // (invisible on a light background). Our own `track` styles paint the real on/off colors on top;
  // "default" just guarantees the MUI fallback is a visible grey rather than white. Callers can
  // still override `color`.
  return (
    <MuiSwitch
      color="default"
      className={classnames(className, { [primary]: switchColor === 'primary' })}
      {...(muiProps as any)}
      disableRipple
      classes={classes}
      ref={ref}
    />
  )
})

export default Switch
